// "MMM YYYY" in English ("Oct 2026"), in the viewer's zone: the month the
// member joined, which `auth.js#setAnalytics` hands to `analytics.identify`.
// `Intl`, not a date library: this model is on every page's boot path.
const MONTH_YEAR = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" });

// `created_at` is a timestamp (`Web::Session#as_json`), so `Date` parses it
// exactly; an absent one reads as now and an unparseable one as "Invalid
// date", the forms a session envelope has always produced.
function monthYear(createdAt) {
  const date = createdAt === undefined ? new Date() : new Date(createdAt ?? NaN);
  return Number.isNaN(date.getTime()) ? "Invalid date" : MONTH_YEAR.format(date);
}

export default class User {
  constructor(data) {
    this.host = data.host;
    this.cypress = data.cypress;
    this.id = data.id;
    this.actorId = data.actorId;
    this.token = data.token;
    this.admin = data.admin;
    this.defaultGeographyMode = data.defaultGeographyMode;
    this.networkingAllConsent = data.networkingAllConsent;
    this.networkingFriendOfFriendsConsent =
      data.networkingFriendOfFriendsConsent;
    this.networkingHubMembersConsent = data.networkingHubMembersConsent;
    this.networkingReadersConsent = data.networkingReadersConsent;
    this.signedIn = data.signedIn;
    this.productTier = data.productTier;
    this.billingFrequency = "monthly";
    this.userCreatedAt = data.userCreatedAt;
    this.sessionFetchedAt = data.lastActiveAt;
    this.emailVerificationState = data.emailVerificationState;
    // Whether the member has an enrolled passkey (`Web::Session#has_passkey?`,
    // passwordless-sign-in D6). StepUpAuth reads this to offer "Confirm with
    // passkey" first; false for a stale cached session that predates this key,
    // which is the safe default — it just falls back to the email-code prompt.
    this.hasPasskey = data.hasPasskey || false;
    this.presence = data.presence;
    this.linked = data.linked;
    this.linkedContactId = data.linkedContactId;
    this.linkRequestPending = data.linkRequestPending;
    this.signupMonthYear = monthYear(data.userCreatedAt);
    this.name = data.name;
    this.firstName = data.firstName;
    this.email = data.email;
    this.reputation = data.reputation;
    this.reputable = data.reputable;
    this.correctPercentage = data.correctPercentage;
    this.availableBalance = data.availableBalance;
    this.validationSkips = data.validationSkips;
    this.avatarUrl = data.avatarUrl || null;
    // This constructor is a WHITELIST: a key the server stamps but no line here
    // names never reaches the client. The three below are stamped by
    // `Web::Session#as_json` — `pulse` (`Web::Session#pulse?`, read by the user
    // store's `pulseCapability`) and the roster fragment
    // `Web::TeamContactPayload` the Contact Us menu renders from
    // (artemis-human-handoff D8). Each defaults to the shape its reader expects
    // for an envelope that omits it, so no consumer has to test for absence.
    this.pulse = data.pulse || false;
    this.teamContacts = data.teamContacts || [];
    this.teamEmail = data.teamEmail || null;
    // The upload-cap table (media-economics D10), stamped by
    // `Web::Session#attachment_caps`. Null when a stale cached session
    // predates this key — every reader treats null as "no client-side limit"
    // rather than crashing or typing a number of its own.
    this.attachmentCaps = data.attachmentCaps || null;
    // The author's own message-intelligence preference, stamped by
    // `Web::Session#message_intel_enabled` (we-message-ingestion I10). THREE-
    // VALUED on the client and therefore `??`, never `||`: `false` is the
    // author's opt-out and must survive construction, while `null` means no
    // envelope resolved the fact at all (a guest session, or a cached one that
    // predates this key). A reader that collapsed those two would tell an
    // author Artemis skips their messages when nothing ever said so.
    this.messageIntelEnabled = data.messageIntelEnabled ?? null;
    // The member's own "Show me on the map" preference, stamped by
    // `Web::Session#as_json` off `users.map_presence_visible` (NOT NULL,
    // default true). `??`, never `||`, for the same reason as the line above:
    // `false` is the member's opt-out and must survive, while `null` means no
    // envelope stated the fact, so the Account Settings toggle renders it
    // unresolved rather than claiming either state.
    this.mapPresenceVisible = data.mapPresenceVisible ?? null;
    // The active one-shot login notices (`Web::Session#user_notices`) the
    // `UserNoticeBanner` renders, and the sign-up gift (platform-budgets D10):
    // its state, stamped units and the server-formatted dollar label the
    // granted notice names. Null gift fields are a user never enrolled.
    this.userNotices = data.userNotices || [];
    this.signupGiftState = data.signupGiftState ?? null;
    this.signupGiftUnits = data.signupGiftUnits ?? null;
    this.signupGiftUsdLabel = data.signupGiftUsdLabel ?? null;
    // The role the member stated at the door (`users.stated_role`, one of
    // `WaitlistEntry::ROLES`), which the showcase picker preselects by. Null is
    // a member who stated none (registered before the waitlist door).
    this.statedRole = data.statedRole ?? null;
    this.sessionGeographyIntents = 0;
  }
}
